import { getCoverageLinesText } from '@common/utils';
import moment from 'moment/moment';
import { LocalStorage } from 'enums';
import { messages } from 'i18n';
import { CarrierSubmissionSearchItem } from 'types';
import { Sorters } from 'utils';

export enum QueueFilterType {
  Insured = 'insuredName',
  Lob = 'coverageLines',
  EffectiveDate = 'effectiveDate',
  ApprovalStatus = 'approvalStatus',
}

const QueueStoragePrefix = LocalStorage.SubmissionQueue;

export const queueStorageKeys = {
  insured: `${QueueStoragePrefix}_${QueueFilterType.Insured}`,
  coverageLine: `${QueueStoragePrefix}_${QueueFilterType.Lob}`,
  effectiveDate: `${QueueStoragePrefix}_${QueueFilterType.EffectiveDate}`,
  approvalStatus: `${QueueStoragePrefix}_${QueueFilterType.ApprovalStatus}`,
  sort: `${QueueStoragePrefix}_sort`,
} as const;

export enum QueueSortType {
  ReceivedNewest = 'ReceivedNewest',
  ReceivedOldest = 'ReceivedOldest',
  InsuredAsc = 'InsuredAsc',
  InsuredDesc = 'InsuredDesc',
  AgentAsc = 'AgentAsc',
  AgentDesc = 'AgentDesc',
  LobAsc = 'LobAsc',
  LobDesc = 'LobDesc',
  EffectiveDateNewest = 'EffectiveDateNewest',
  EffectiveDateOldest = 'EffectiveDateOldest',
}

export const queueSorters: Sorters<CarrierSubmissionSearchItem, QueueSortType> = {
  [QueueSortType.ReceivedNewest]: {
    sorter: (a, b) => moment(b.submission?.createdAt).diff(moment(a.submission?.createdAt)),
    label: 'Newest first',
  },
  [QueueSortType.ReceivedOldest]: {
    sorter: (a, b) => moment(a.submission?.createdAt).diff(moment(b.submission?.createdAt)),
    label: 'Oldest first',
  },
  [QueueSortType.InsuredAsc]: {
    sorter: (a, b) => (a.submission?.insuredName || '').localeCompare(b.submission?.insuredName || ''),
    label: 'A-Z',
  },
  [QueueSortType.InsuredDesc]: {
    sorter: (a, b) => (b.submission?.insuredName || '').localeCompare(a.submission?.insuredName || ''),
    label: 'Z-A',
  },
  [QueueSortType.AgentAsc]: {
    sorter: (a, b) => (a.agentName || '').localeCompare(b.agentName || ''),
    label: 'A-Z',
  },
  [QueueSortType.AgentDesc]: {
    sorter: (a, b) => (b.agentName || '').localeCompare(a.agentName || ''),
    label: 'Z-A',
  },
  [QueueSortType.LobAsc]: {
    sorter: (a, b) =>
      (getCoverageLinesText(a.submission?.coverageLines) || '').localeCompare(
        getCoverageLinesText(b.submission?.coverageLines) || '',
      ),
    label: 'A-Z',
  },
  [QueueSortType.LobDesc]: {
    sorter: (a, b) =>
      (getCoverageLinesText(b.submission?.coverageLines) || '').localeCompare(
        getCoverageLinesText(a.submission?.coverageLines) || '',
      ),
    label: 'Z-A',
  },
  [QueueSortType.EffectiveDateNewest]: {
    sorter: (a, b) => moment(b.submission?.dueDate).diff(moment(a.submission?.dueDate)),
    label: 'Newest first',
  },
  [QueueSortType.EffectiveDateOldest]: {
    sorter: (a, b) => moment(a.submission?.dueDate).diff(moment(b.submission?.dueDate)),
    label: 'Oldest first',
  },
};

type QueueColumnConfig = {
  key: string;
  title: string;
  flex: number | string;
};

export const QueueColumns = {
  Received: 'received',
  InsuredName: 'insuredName',
  Agent: 'agent',
  LineOfBusiness: 'lineOfBusiness',
  ApprovalStatus: 'approvalStatus',
  ClearanceOutput: 'clearanceOutput',
  EffectiveDate: 'effectiveDate',
  TriageOutput: 'triageOutput',
  StpEnabled: 'stpEnabled',
} as const;

type QueueColumnsKeys = keyof typeof QueueColumns;
export type QueueColumnsValues = (typeof QueueColumns)[QueueColumnsKeys];

export const QueueSortTypeMap: Record<string, { asc: QueueSortType; desc: QueueSortType }> = {
  [QueueColumns.Received]: { asc: QueueSortType.ReceivedOldest, desc: QueueSortType.ReceivedNewest },
  [QueueColumns.InsuredName]: { asc: QueueSortType.InsuredAsc, desc: QueueSortType.InsuredDesc },
  [QueueColumns.Agent]: { asc: QueueSortType.AgentAsc, desc: QueueSortType.AgentDesc },
  [QueueColumns.LineOfBusiness]: { asc: QueueSortType.LobAsc, desc: QueueSortType.LobDesc },
  [QueueColumns.EffectiveDate]: { asc: QueueSortType.EffectiveDateOldest, desc: QueueSortType.EffectiveDateNewest },
};

export const queueColumnsConfig: Record<QueueColumnsValues, QueueColumnConfig> = {
  [QueueColumns.Received]: {
    key: QueueColumns.Received,
    title: messages.submissionQueuePage.columnHeaders.received,
    flex: '0 0 116px',
  },
  [QueueColumns.InsuredName]: {
    key: QueueColumns.InsuredName,
    title: messages.submissionQueuePage.columnHeaders.insured,
    flex: 1,
  },
  [QueueColumns.Agent]: {
    key: QueueColumns.Agent,
    title: messages.submissionQueuePage.columnHeaders.agent,
    flex: 1,
  },
  [QueueColumns.LineOfBusiness]: {
    key: QueueColumns.LineOfBusiness,
    title: messages.submissionQueuePage.columnHeaders.lineOfBusiness,
    flex: 1,
  },
  [QueueColumns.ApprovalStatus]: {
    key: QueueColumns.ApprovalStatus,
    title: messages.submissionQueuePage.columnHeaders.approvalStatus,
    flex: 1,
  },
  [QueueColumns.EffectiveDate]: {
    key: QueueColumns.EffectiveDate,
    title: messages.submissionQueuePage.columnHeaders.effectiveDate,
    flex: 1,
  },
  [QueueColumns.ClearanceOutput]: {
    key: QueueColumns.ClearanceOutput,
    title: messages.submissionQueuePage.columnHeaders.clearanceOutput,
    flex: 1,
  },
  [QueueColumns.TriageOutput]: {
    key: QueueColumns.TriageOutput,
    title: messages.submissionQueuePage.columnHeaders.triageOutput,
    flex: 1,
  },
  [QueueColumns.StpEnabled]: {
    key: QueueColumns.StpEnabled,
    title: messages.submissionQueuePage.columnHeaders.stpEnabled,
    flex: '0 0 80px',
  },
};
